import { UtilityRoomAmenityType, utilityRoomAmenityTypes } from "@instacover/helpers"
import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Trans } from "react-i18next"

import { AmenitiesForm } from "@/components/amenities/AmenitiesForm"
import ArrowButton from "@/components/base/ArrowButton"
import { Spinner } from "@/components/Spinner"
import { db, getNextAmenity } from "@/database/database"
import { useOrderedSteps } from "@/database/hooks"
import { amenityFormSearchSchema } from "@/helpers/zodSchemes"
import { useGetSessionDetail } from "@/hooks/api/useGetSessionDetail"
import { useSetAmenities } from "@/hooks/api/useSetAmenities"
import { useStepsRouting } from "@/hooks/useStepsRouting"
import { BaseLayout } from "@/sections/layouts/BaseLayout"
import { isUtilityRoomAmenity } from "@/types/amenityTypes"
import { logger } from "@/utils/logging"

export const Route = createFileRoute("/$sessionId/_session/_steps/utilityRoom/")({
  component: UtilityRoomPage,
  validateSearch: (search) => amenityFormSearchSchema.parse(search),
})

function UtilityRoomPage() {
  const { sessionId } = Route.useParams()
  const { viewMode } = Route.useSearch()
  const navigate = useNavigate()

  /* Navigation to next step */
  const { firstUnfinishedStepLink } = useStepsRouting(sessionId)
  const { data: orderedSteps } = useOrderedSteps(sessionId)
  const utilityRoomStep = useMemo(() => orderedSteps?.find((step) => step.name === "UtilityRoom"), [orderedSteps])

  useEffect(() => {
    if (!utilityRoomStep || !firstUnfinishedStepLink) return

    if (utilityRoomStep.isFinished) {
      navigate(firstUnfinishedStepLink)
    }
  }, [navigate, firstUnfinishedStepLink, utilityRoomStep])

  /* Navigation to next amenity in current step */
  useEffect(() => {
    // If there is amenity without photo and not skipped, navigate to it
    async function navigateToNextAmenity() {
      const nextAmenity = await getNextAmenity<UtilityRoomAmenityType>(sessionId, "UtilityRoom")
      if (nextAmenity) {
        navigate({ to: "/$sessionId/utilityRoom/$amenityType", params: { sessionId, amenityType: nextAmenity.type } })
      }
    }

    navigateToNextAmenity()
  }, [navigate, sessionId])

  /* Amenities */
  const { data: sessionDetail, isLoading: isLoadingSessionDetail } = useGetSessionDetail(sessionId)

  const initialAmenities = useMemo(() => {
    if (!sessionDetail?.amenities) return []

    return sessionDetail.amenities
      .filter(isUtilityRoomAmenity)
      .filter((a) => !a.deletedAt)
      .map((a) => a.type)
  }, [sessionDetail])

  if (isLoadingSessionDetail || !sessionDetail) {
    return <BaseLayout descriptionNode={<Spinner position="center" />} />
  }

  return (
    <UtilityRoomForm
      sessionId={sessionId}
      initialAmenities={initialAmenities}
      viewMode={viewMode === "add" ? "add" : "create"}
    />
  )
}

type UtilityRoomFormProps = {
  initialAmenities: UtilityRoomAmenityType[]
  viewMode: "create" | "add"
  sessionId: string
}

function UtilityRoomForm(props: UtilityRoomFormProps) {
  const { sessionId, initialAmenities, viewMode } = props

  const navigate = useNavigate()

  const onSkip = useCallback(async () => {
    await db.orderedStep.update({ name: "UtilityRoom", instahomeSessionId: sessionId, data: { isFinished: true } })
  }, [sessionId])

  /* Amenity selection state */
  const [selectedAmenities, setSelectedAmenities] = useState<UtilityRoomAmenityType[]>(initialAmenities)

  const handleToggleAmenity = (amenity: UtilityRoomAmenityType) => {
    setSelectedAmenities((prev) => {
      if (prev.includes(amenity)) {
        return prev.filter((a) => a !== amenity)
      }

      return [...prev, amenity]
    })
  }

  const { mutateAsync: setAmenitiesAsync } = useSetAmenities()

  const amenitiesOptions = useMemo(() => {
    if (viewMode === "add") {
      const notSelectedAmenities = utilityRoomAmenityTypes.filter(
        (amenityType) => !initialAmenities.includes(amenityType),
      )

      return notSelectedAmenities
    }

    return utilityRoomAmenityTypes
  }, [initialAmenities, viewMode])

  // TODO ak pouzivatel odnastavi(vymaze) nejake amenities ktore mal (z overview, otvorenie na novom zariadeni), premazat vsetky fotky ktore patria ku zmazanym amenities
  async function onContinue() {
    const res = await setAmenitiesAsync({
      sessionId,
      amenityTypes: selectedAmenities,
      amenityGroup: "UtilityRoom",
      mode: viewMode,
    })

    await db.amenity.replace(res.amenities)

    const nextAmenity = await getNextAmenity<UtilityRoomAmenityType>(sessionId, "UtilityRoom")
    if (!nextAmenity) {
      logger.error("No next amenity found after creating amenities")
      return
    }

    navigate({ to: "/$sessionId/utilityRoom/$amenityType", params: { sessionId, amenityType: nextAmenity.type } })
  }

  const onBackToOverview = useCallback(async () => {
    await db.orderedStep.update({ name: "UtilityRoom", instahomeSessionId: sessionId, data: { isFinished: true } })
    navigate({ to: "/$sessionId/overview", params: { sessionId }, replace: true })
  }, [navigate, sessionId])

  return (
    <BaseLayout
      descriptionNode={
        <div>
          <div className="mb-9">
            <Heading as="h1" variant="h1" className="mb-5">
              <Trans i18nKey="utilityRoom.form.heading" />
            </Heading>

            <Text>
              <Trans
                i18nKey="utilityRoom.form.description"
                components={{
                  span: <span className="text-ic-primary font-bold uppercase" />,
                }}
              />
            </Text>
          </div>

          <AmenitiesForm options={amenitiesOptions} selectedValues={selectedAmenities} onToggle={handleToggleAmenity} />
        </div>
      }
      buttons={
        <div className="flex w-full justify-center gap-4">
          {viewMode === "create" && (
            <Button type="button" variant="secondary" onClick={onSkip} data-testid="amenity-form-skip-btn">
              <Trans i18nKey="general.skip" />
            </Button>
          )}

          {viewMode === "add" && (
            <Button type="button" variant="secondary" onClick={onBackToOverview} data-testid="amenity-form-back-btn">
              <Trans i18nKey="general.back" />
            </Button>
          )}

          <ArrowButton
            variant="primary"
            shape="wide"
            disabled={selectedAmenities.length === initialAmenities.length}
            onClick={onContinue}
            data-testid="amenity-form-continue-btn"
          />
        </div>
      }
    />
  )
}
